/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { useTheme } from '@mui/material';
import React from 'react';
import { CustomToggleButton, CustomTooltip } from '@opengeoweb/shared';
import type { Concept } from '../../store/types';
import { useDataExplorerTranslation } from '../../utils/i18n';

const style = {
  borderRadius: '20px!important',
  marginRight: '4px',
  whiteSpace: 'nowrap',
  height: '24px!important',
  padding: '5px 12px!important',
  textTransform: 'none!important',
  fontWeight: 'normal',
  marginTop: '4px',
};

interface ConceptChipProps {
  concept: Concept;
  isDisabled?: boolean;
  toggleChip?: (concept: string) => void;
  isInSearch?: boolean;
}

export const ConceptChip: React.FC<ConceptChipProps> = ({
  concept,
  isDisabled,
  toggleChip,
  isInSearch,
}: ConceptChipProps) => {
  const { t } = useDataExplorerTranslation();
  const {
    palette: { geowebColors },
  } = useTheme();
  const chip = concept.id;

  const translatedLabel = t(concept.label);

  return (
    <CustomTooltip title={translatedLabel}>
      <span>
        <CustomToggleButton
          data-testid={`conceptChip-${concept.id}-${isInSearch ? 'inSearch' : 'default'}`}
          disabled={isDisabled}
          onClick={(): void => toggleChip!(chip!)}
          selected={Boolean(concept.selected)}
          sx={{
            ...style,
            ...(isInSearch && {
              backgroundColor: `${geowebColors.chip.highlightDefault.rgba}!important`,
              '&:hover': {
                backgroundColor: `${geowebColors.chip.highlightDefaultMouseOver.rgba}!important`,
              },
              ...(concept.selected && {
                backgroundColor: `${geowebColors.chip.highlightActiveDefault.rgba}!important`,
                '&:hover': {
                  backgroundColor: `${geowebColors.chip.highlightActiveMouseOver.rgba}!important`,
                },
              }),
            }),
          }}
          tabIndex={0}
          variant="boxed"
        >
          {translatedLabel}
        </CustomToggleButton>
      </span>
    </CustomTooltip>
  );
};
